import { useState, useEffect } from 'react';
import { Box, Typography, Button, Stack, Divider, TextField, Select, MenuItem, FormControl, Alert, CircularProgress, IconButton, InputAdornment } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ProfileHeader from '../components/profile/ProfileHeader';
import { createProfile } from '../services/profileService';
import { getAuthUser, getGoogleInfo, removeGoogleInfo } from '../utils/tokenManager';
import { trackProfileCreated } from '../utils/analytics';

const PageContainer = styled(Box)(() => ({
  minHeight: '100vh',
  backgroundColor: '#F9FAFB',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column'
}));

const MainContent = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'grid',
  gridTemplateColumns: '1fr',
  paddingTop: theme.spacing(12),
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: '1fr 1fr',
    paddingTop: 0
  }
}));

const LeftPanel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4, 2),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(6, 4)
  },
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(12, 6),
    overflowY: 'auto',
    maxHeight: '100vh'
  }
}));

const RightPanel = styled(Box)(() => ({
  display: 'none',
  '@media (min-width: 1280px)': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4rem 2rem',
    backgroundColor: '#F9FAFB'
  }
}));

const ContentContainer = styled(Box)(() => ({
  width: '100%',
  maxWidth: 800,
  display: 'flex',
  flexDirection: 'column'
}));

const PrimaryButton = styled(Button)(() => ({
  height: 48,
  backgroundColor: '#4F46E5',
  color: 'white',
  fontWeight: 600,
  fontSize: '0.875rem',
  textTransform: 'none',
  borderRadius: '8px',
  boxShadow: 'none',
  paddingLeft: 32,
  paddingRight: 32,
  '&:hover': {
    backgroundColor: '#4338CA',
    boxShadow: 'none'
  },
  '&:disabled': {
    backgroundColor: '#D1D5DB',
    color: '#9CA3AF'
  },
  transition: 'background-color 0.2s ease'
}));

const countryOptions = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'AU', label: 'Australia' },
  { value: 'IN', label: 'India' }
];

const CompleteProfile = () => {
  const navigate = useNavigate();

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [country, setCountry] = useState('United States');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Track if user signed up via OAuth (Google, Facebook, etc.) - no password needed
  // Email and phone signups still require passwords
  const [isGoogleSignup, setIsGoogleSignup] = useState(false);
  
  // Store auth provider to send to profile API
  const [authProvider, setAuthProvider] = useState<string>('email');
  
  // Track which field should be disabled based on signup method
  const [signupMethod, setSignupMethod] = useState<'email' | 'phone'>('email');
  
  // Debug render
  console.log('🎨 CompleteProfile rendering - isOAuthSignup (isGoogleSignup):', isGoogleSignup);

  // Carousel state
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = [
    {
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&auto=format&fit=crop',
      title: 'Welcome to RiteReview',
      description: 'Join thousands of businesses managing their online reputation and growing their customer base with our powerful review management platform.'
    },
    {
      image: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&auto=format&fit=crop',
      title: 'Streamline your workflow',
      description: 'Collect, manage, and respond to reviews from all platforms in one centralized dashboard. Save time and boost efficiency.'
    },
    {
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&auto=format&fit=crop',
      title: 'Data-driven insights',
      description: 'Track your reputation score, analyze customer sentiment, and make informed decisions to improve your business performance.'
    }
  ];

  // Load user data from registration
  useEffect(() => {
    console.log('🔍 CompleteProfile - useEffect running');
    console.log('🔍 Initial isGoogleSignup state:', isGoogleSignup);
    
    // Check stored user data
    const user = getAuthUser();
    console.log('📧 CompleteProfile - Loaded user:', user);
    console.log('📧 CompleteProfile - Auth provider:', user?.auth_provider);
    
    if (user) {
      // Pre-fill user information
      console.log('📧 CompleteProfile - Setting email to:', user.email);
      setEmail(user.email || '');
      setPhoneNumber(user.phone || '');
      if (user.first_name) setFirstName(user.first_name);
      if (user.last_name) setLastName(user.last_name);
      
      // Store auth provider for API submission
      const provider = user.auth_provider || 'email';
      setAuthProvider(provider);
      console.log('📝 Auth provider stored for API:', provider);
      
      // Determine signup method based on which field is populated
      // If user has phone but no email (or auth_provider is 'phone' or 'sms'), they signed up with phone
      // If user has email (or auth_provider is email/google/etc), they signed up with email/OAuth
      const hasPhone = !!user.phone && user.phone.trim() !== '';
      const hasEmail = !!user.email && user.email.trim() !== '';
      const isPhoneSignup = (provider === 'phone' || provider === 'sms') || (hasPhone && !hasEmail);
      
      if (isPhoneSignup) {
        setSignupMethod('phone');
        console.log('📱 Phone signup detected - phone field will be DISABLED (greyed), email EDITABLE');
        console.log('   → hasPhone:', hasPhone, '| hasEmail:', hasEmail, '| provider:', provider);
      } else {
        setSignupMethod('email');
        console.log('📧 Email/OAuth signup detected - email field will be DISABLED (greyed), phone EDITABLE');
        console.log('   → hasPhone:', hasPhone, '| hasEmail:', hasEmail, '| provider:', provider);
      }
      
      // Check auth provider - hide password ONLY for OAuth providers (google, facebook, etc.)
      // Email and phone still require passwords
      const oauthProviders = ['google', 'facebook', 'apple', 'microsoft'];
      const isOAuthSignup = user.auth_provider && oauthProviders.includes(user.auth_provider.toLowerCase());
      
      if (isOAuthSignup) {
        console.log('✅ OAuth provider detected (auth_provider:', user.auth_provider + ')');
        console.log('✅ Password field will be HIDDEN');
        setIsGoogleSignup(true); // No password needed for OAuth signups
      } else {
        console.log('ℹ️ Email/Phone auth detected (auth_provider:', user.auth_provider + ') - password field will be shown');
        setIsGoogleSignup(false); // Password required for email/phone signups
      }
      
      console.log('🎯 Final state: signupMethod will be:', isPhoneSignup ? 'phone' : 'email');
    } else {
      console.log('⚠️ No user data found in storage');
    }
  }, []);

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [slides.length]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      // Validate passwords only for non-Google signups
      if (!isGoogleSignup) {
      // Validate passwords match
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        setIsLoading(false);
        return;
      }

      // Validate password requirements
      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
      if (!passwordRegex.test(password)) {
        setError('Password must contain at least 8 characters, one uppercase, one lowercase, one number, and one special character.');
        setIsLoading(false);
        return;
        }
      }

      // Build profile data
      const profileData: any = {
        first_name: firstName,
        second_name: lastName,
        business_name: businessName,
        phone_number: phoneNumber,
        email: email,
        business_email: email,
        country: country,
        state: state,
        city: city,
        zipcode: zipCode,
        auth_type: authProvider  // Include auth provider (google, facebook, email, phone)
      };

      console.log('📤 Profile data being sent to API:', profileData);
      console.log('📤 Auth type:', authProvider);

      // Only include password for non-OAuth signups
      if (!isGoogleSignup) {
        profileData.password = password;
        console.log('📤 Password included in payload');
      } else {
        console.log('📤 Password NOT included (OAuth signup)');
      }

      // Call API to create profile
      const response = await createProfile(profileData);

      console.log('Profile created successfully:', response);

      // Track profile creation in Google Analytics
      trackProfileCreated(authProvider, response.data?.profile?.id || response.data?.id);

      // Clear Google info after successful profile creation
      removeGoogleInfo();

      setSuccess('Profile completed successfully! Redirecting to dashboard...');

      // Navigate to dashboard immediately after successful completion
      navigate('/dashboard');

    } catch (err) {
      console.error('Profile completion error:', err);
      setError(err instanceof Error ? err.message : 'Failed to complete profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageContainer>
      <ProfileHeader />
      <MainContent>
        <LeftPanel>
        <ContentContainer>
          {/* Page Header */}
          <Box sx={{ mb: 6 }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                lineHeight: 1.2,
                color: '#111827',
                mb: 1.5,
                letterSpacing: '-0.02em'
              }}
            >
              Complete Your Profile
            </Typography>
            <Typography
              sx={{
                color: '#6B7280',
                fontSize: '1rem',
                lineHeight: 1.6
              }}
            >
              Please provide the following details to set up your business account.
            </Typography>
          </Box>

          {/* Alerts */}
              {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 4 }}>
                  {error}
                </Alert>
              )}
              {success && (
            <Alert severity="success" sx={{ mb: 4 }}>
                  {success}
                </Alert>
              )}

          {/* Form */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              bgcolor: 'white',
              borderRadius: '16px',
              border: '1px solid #E5E7EB',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
              p: { xs: 3, sm: 4, md: 6 }
            }}
          >
            <Stack spacing={4}>
              {/* Section: Owner Information */}
              <Box>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#111827',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    mb: 0.5
                  }}
                >
                  Owner Information
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                    mb: 5
                  }}
                >
                  Your personal details
                </Typography>

                <Stack spacing={4}>
                  {/* First & Last Name */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                      gap: 3,
                      alignItems: 'start'
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#374151',
                        pt: 1.5
                      }}
                    >
                      First & Last Name
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        fullWidth
                        placeholder="First Name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            '& fieldset': {
                              borderColor: '#D1D5DB'
                            },
                            '&:hover fieldset': {
                              borderColor: '#9CA3AF'
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#4F46E5',
                              borderWidth: '1px'
                            }
                          }
                        }}
                      />
                      <TextField
                        fullWidth
                        placeholder="Last Name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            '& fieldset': {
                              borderColor: '#D1D5DB'
                            },
                            '&:hover fieldset': {
                              borderColor: '#9CA3AF'
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#4F46E5',
                              borderWidth: '1px'
                            }
                          }
                        }}
                    />
                    </Stack>
                  </Box>

                  <Divider sx={{ borderColor: '#E5E7EB' }} />

                  {/* Phone Number */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                      gap: 3,
                      alignItems: 'start'
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#374151',
                        pt: 1.5
                      }}
                    >
                      Phone Number
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder={signupMethod === 'email' ? 'Enter your phone number (optional)' : ''}
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      disabled={signupMethod === 'phone'}
                      InputProps={{
                        readOnly: signupMethod === 'phone'
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          bgcolor: signupMethod === 'phone' ? '#F3F4F6' : 'transparent',
                          '& fieldset': {
                            borderColor: '#D1D5DB'
                          },
                          '&:hover fieldset': {
                            borderColor: signupMethod === 'phone' ? '#D1D5DB' : '#9CA3AF'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#4F46E5',
                            borderWidth: '1px'
                          }
                        }
                      }}
                    />
                  </Box>

                  <Divider sx={{ borderColor: '#E5E7EB' }} />

                  {/* Email Address */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                      gap: 3,
                      alignItems: 'start'
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#374151',
                        pt: 1.5
                      }}
                    >
                      Email Address
                    </Typography>
                    <TextField
                      fullWidth
                      type="email"
                      placeholder={signupMethod === 'phone' ? 'Enter your email address (optional)' : ''}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={signupMethod === 'email'}
                      InputProps={{
                        readOnly: signupMethod === 'email'
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          bgcolor: signupMethod === 'email' ? '#F3F4F6' : 'transparent',
                          '& fieldset': {
                            borderColor: '#D1D5DB'
                          },
                          '&:hover fieldset': {
                            borderColor: signupMethod === 'email' ? '#D1D5DB' : '#9CA3AF'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#4F46E5',
                            borderWidth: '1px'
                          }
                        }
                      }}
                    />
                  </Box>
                </Stack>
              </Box>

              <Divider sx={{ borderColor: '#E5E7EB', my: 2 }} />

              {/* Section: Business Details */}
              <Box>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#111827',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    mb: 0.5
                  }}
                >
                  Business Details
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                    mb: 5
                  }}
                >
                  Information about your business
                </Typography>

                <Stack spacing={4}>
                  {/* Business Name */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                      gap: 3,
                      alignItems: 'start'
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#374151',
                        pt: 1.5
                      }}
                    >
                      Business Name
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter your business name"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          '& fieldset': {
                            borderColor: '#D1D5DB'
                          },
                          '&:hover fieldset': {
                            borderColor: '#9CA3AF'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#4F46E5',
                            borderWidth: '1px'
                          }
                        }
                      }}
                    />
                  </Box>

                  <Divider sx={{ borderColor: '#E5E7EB' }} />

                  {/* Country */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                      gap: 3,
                      alignItems: 'start'
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#374151',
                        pt: 1.5
                      }}
                    >
                      Country
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                      value={country}
                      onChange={(e) => setCountry(e.target.value as string)}
                        sx={{
                          borderRadius: '8px',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#D1D5DB'
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#9CA3AF'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4F46E5',
                            borderWidth: '1px'
                          }
                        }}
                      >
                        {countryOptions.map((option) => (
                          <MenuItem key={option.value} value={option.label}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <Divider sx={{ borderColor: '#E5E7EB' }} />

                  {/* State & City */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                      gap: 3,
                      alignItems: 'start'
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#374151',
                        pt: 1.5
                      }}
                    >
                      State & City
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        fullWidth
                        placeholder="State / Province"
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            '& fieldset': {
                              borderColor: '#D1D5DB'
                            },
                            '&:hover fieldset': {
                              borderColor: '#9CA3AF'
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#4F46E5',
                              borderWidth: '1px'
                            }
                          }
                        }}
                      />
                      <TextField
                        fullWidth
                        placeholder="City"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            '& fieldset': {
                              borderColor: '#D1D5DB'
                            },
                            '&:hover fieldset': {
                              borderColor: '#9CA3AF'
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#4F46E5',
                              borderWidth: '1px'
                            }
                          }
                        }}
                    />
                    </Stack>
                  </Box>

                  <Divider sx={{ borderColor: '#E5E7EB' }} />

                  {/* Zip Code */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                      gap: 3,
                      alignItems: 'start'
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#374151',
                        pt: 1.5
                      }}
                    >
                      Zip / Postal Code
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter zip or postal code"
                      value={zipCode}
                      onChange={(e) => setZipCode(e.target.value)}
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          '& fieldset': {
                            borderColor: '#D1D5DB'
                          },
                          '&:hover fieldset': {
                            borderColor: '#9CA3AF'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#4F46E5',
                            borderWidth: '1px'
                          }
                        }
                      }}
                    />
                  </Box>
                </Stack>
              </Box>

              {/* Only show password section for non-OAuth signups (email/phone require password) */}
              {!isGoogleSignup && (
                <>
                  <Divider sx={{ borderColor: '#E5E7EB', my: 2 }} />

                  {/* Section: Set Password */}
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: '#111827',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        mb: 0.5
                      }}
                    >
                      Set Your Password
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        color: '#6B7280',
                        mb: 5
                      }}
                    >
                      Create a secure password for your account
                    </Typography>

                    <Stack spacing={4}>
                      {/* Create Password */}
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                          gap: 3,
                          alignItems: 'start'
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            color: '#374151',
                            pt: 1.5
                          }}
                        >
                          Create Password
                        </Typography>
                        <TextField
                          fullWidth
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter a secure password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                                  sx={{ color: '#6B7280' }}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                              '& fieldset': {
                                borderColor: '#D1D5DB'
                              },
                              '&:hover fieldset': {
                                borderColor: '#9CA3AF'
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#4F46E5',
                                borderWidth: '1px'
                              }
                            }
                          }}
                        />
                      </Box>

                      <Divider sx={{ borderColor: '#E5E7EB' }} />

                      {/* Confirm Password */}
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                          gap: 3,
                          alignItems: 'start'
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            color: '#374151',
                            pt: 1.5
                          }}
                        >
                          Confirm Password
                        </Typography>
                        <Box>
                          <TextField
                            fullWidth
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              edge="end"
                                    sx={{ color: '#6B7280' }}
                            >
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                          }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                '& fieldset': {
                                  borderColor: '#D1D5DB'
                                },
                                '&:hover fieldset': {
                                  borderColor: '#9CA3AF'
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#4F46E5',
                                  borderWidth: '1px'
                                }
                              }
                            }}
                          />
                    <Typography
                      sx={{
                              color: '#6B7280',
                        fontSize: '0.75rem',
                              mt: 1.5
                      }}
                    >
                      Password must contain at least 8 characters, one uppercase, one lowercase, one number, and one special character.
                    </Typography>
                        </Box>
                      </Box>
                    </Stack>
                  </Box>

                  <Divider sx={{ borderColor: '#E5E7EB', my: 2 }} />
                </>
              )}

              {/* Submit Button */}
              <Box sx={{ pt: 2 }}>
                <PrimaryButton type="submit" disabled={isLoading}>
                  {isLoading ? (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CircularProgress size={20} color="inherit" />
                      <span>Saving...</span>
                    </Stack>
                  ) : (
                    'Save & Continue'
                  )}
                </PrimaryButton>
              </Box>
            </Stack>
          </Box>

          {/* Terms */}
                <Typography
                  sx={{
                    textAlign: 'center',
              color: '#6B7280',
              fontSize: '0.875rem',
              mt: 4
                  }}
                >
                  By clicking "Save & Continue", you agree to our{' '}
            <Box
              component="a"
              href="#"
              sx={{
                fontWeight: 500,
                color: '#4F46E5',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
                    Terms of Service
            </Box>{' '}
                  and{' '}
            <Box
              component="a"
              href="#"
              sx={{
                fontWeight: 500,
                color: '#4F46E5',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
                    Privacy Policy
            </Box>
                  .
                </Typography>
          </ContentContainer>
        </LeftPanel>

        <RightPanel>
          <Box sx={{ position: 'relative', maxWidth: 600 }}>
            {/* Carousel Images */}
            <Box
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '12px',
                mb: 4
              }}
            >
              {slides.map((slide, index) => (
                <Box
                  key={index}
                  sx={{
                    display: currentSlide === index ? 'block' : 'none',
                    transition: 'opacity 0.5s ease-in-out',
                    opacity: currentSlide === index ? 1 : 0
                  }}
                >
                  <Box
                    component="img"
                    src={slide.image}
                    alt={slide.title}
                    sx={{
                      width: '100%',
                      maxWidth: 600,
                      height: 'auto',
                      mx: 'auto',
                      display: 'block',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                    }}
                  />
                </Box>
              ))}
              </Box>

            {/* Carousel Content */}
            <Box sx={{ maxWidth: 672, mx: 'auto', textAlign: 'center' }}>
              <Typography
                sx={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: '#111827',
                  mb: 1.25,
                  transition: 'opacity 0.3s ease-in-out',
                  minHeight: '2rem'
                }}
              >
                {slides[currentSlide].title}
              </Typography>
              <Typography
                sx={{
                  fontSize: '1rem',
                  lineHeight: 1.6,
                  color: '#6B7280',
                  mb: 5,
                  transition: 'opacity 0.3s ease-in-out',
                  minHeight: '3rem'
                }}
              >
                {slides[currentSlide].description}
              </Typography>

              {/* Carousel Indicators */}
              <Stack direction="row" spacing={1.5} justifyContent="center" alignItems="center">
                {slides.map((_, index) => (
                  <Box
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    sx={{
                      width: currentSlide === index ? 80 : 48,
                      height: 6,
                      borderRadius: '999px',
                      bgcolor: currentSlide === index ? '#F97316' : '#E5E7EB',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: currentSlide === index ? '#F97316' : '#D1D5DB'
                      }
                    }}
                  />
                ))}
            </Stack>
            </Box>
          </Box>
        </RightPanel>
      </MainContent>
    </PageContainer>
  );
};

export default CompleteProfile;
